import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getBatches, createBatch, addInvoicesToBatch } from "../../services/dataService";
import { formatApiErrorDetail } from "../../lib/api";
import { formatCurrency } from "../../lib/format";
import { Loader2, Layers } from "lucide-react";
import { toast } from "sonner";

export function AddToBatchModal({ open, onClose, invoiceIds, onSuccess }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("new");
  const [batchName, setBatchName] = useState("");
  const [existingId, setExistingId] = useState("");

  const { data: batches = [] } = useQuery({ queryKey: ["batches"], queryFn: getBatches, enabled: open });
  const openBatches = batches.filter((b) => b.status === "Open");

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === "new") {
        return createBatch({ name: batchName.trim() || null, invoice_ids: invoiceIds });
      }
      return addInvoicesToBatch(existingId, invoiceIds);
    },
    onSuccess: (batch) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["batches"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(`${invoiceIds.length} invoice(s) added to ${batch.name}`);
      onClose();
      setBatchName("");
      setExistingId("");
      setMode("new");
      onSuccess?.();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add to batch"),
  });

  const canSubmit = mode === "new" ? true : !!existingId;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="add-to-batch-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-blue-600" /> Add to Batch</DialogTitle>
          <DialogDescription>Adding {invoiceIds.length} selected invoice(s) to a batch.</DialogDescription>
        </DialogHeader>

        <RadioGroup value={mode} onValueChange={setMode} className="space-y-3 py-2">
          <div className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <RadioGroupItem value="new" id="mode-new" data-testid="batch-mode-new" className="mt-0.5" />
            <div className="flex-1">
              <Label htmlFor="mode-new" className="font-semibold cursor-pointer">Create a new batch</Label>
              {mode === "new" && (
                <Input value={batchName} onChange={(e) => setBatchName(e.target.value)} placeholder="Batch name (optional)" className="mt-2" data-testid="new-batch-name-input" />
              )}
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
            <RadioGroupItem value="existing" id="mode-existing" data-testid="batch-mode-existing" className="mt-0.5" disabled={openBatches.length === 0} />
            <div className="flex-1">
              <Label htmlFor="mode-existing" className="font-semibold cursor-pointer">Add to an existing open batch</Label>
              {openBatches.length === 0 && <p className="text-xs text-slate-400 mt-1">No open batches available.</p>}
              {mode === "existing" && openBatches.length > 0 && (
                <Select value={existingId} onValueChange={setExistingId}>
                  <SelectTrigger className="mt-2" data-testid="existing-batch-select"><SelectValue placeholder="Select a batch" /></SelectTrigger>
                  <SelectContent>
                    {openBatches.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`existing-batch-option-${b.id}`}>
                        {b.name} · {b.invoice_count} inv · {formatCurrency(b.total_amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-to-batch-cancel">Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit || mutation.isPending} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white" data-testid="add-to-batch-confirm">
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to Batch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
